<template>
    <div class="ai-service-shell">
        <transition name="ai-teaser-fade">
            <div v-if="proactiveVisible && !dialogVisible" class="ai-product-teaser" role="dialog"
                aria-label="AI product consultation">
                <button type="button" class="ai-teaser-main" @click="openChat">
                    <img src="@/assets/img/kefu2.png" alt="">
                    <span><strong>Interested in this product?</strong><small>Ask about price, specifications, shipping or support</small></span>
                </button>
                <button type="button" class="ai-teaser-close" aria-label="Close message" @click="dismissProactive">×</button>
            </div>
        </transition>

        <el-dialog :visible.sync="dialogVisible" :width="dialogWidth" append-to-body
            custom-class="ai-chat-dialog" :close-on-click-modal="false" @open="handleOpen">
            <div slot="title" class="ai-chat-title">
                <img src="@/assets/img/kefu2.png" alt="">
                <div>
                    <strong>AI Customer Service</strong>
                    <span>{{ productContext ? `Discussing: ${productContext}` : 'Products, export prices, cooperation and support' }}</span>
                </div>
                <button type="button" class="ai-contact-button" @click="openLeadForm">Share Contact</button>
            </div>

            <div class="ai-chat-layout">
                <section class="ai-chat-panel" aria-label="Customer service chat">
                    <div ref="messageList" class="ai-message-list" aria-live="polite">
                        <div v-for="message in messages" :key="message.id" :class="['ai-message', `is-${message.role}`]">
                            <img v-if="message.role === 'assistant'" src="@/assets/img/kefu2.png" alt="">
                            <div class="ai-message-bubble">
                                <span>{{ message.text }}</span>
                                <button v-if="message.suggestContact" type="button" class="ai-message-contact"
                                    @click="openLeadForm">Share contact for sales follow-up</button>
                            </div>
                        </div>
                        <div v-if="sending" class="ai-message is-assistant">
                            <img src="@/assets/img/kefu2.png" alt="">
                            <div class="ai-message-bubble ai-typing"><i></i><i></i><i></i></div>
                        </div>
                    </div>

                    <div class="ai-quick-prompts" aria-label="Suggested questions">
                        <button v-for="prompt in quickPrompts" :key="prompt" type="button"
                            :disabled="sending" @click="sendPrompt(prompt)">{{ prompt }}</button>
                    </div>

                    <div class="ai-composer">
                        <el-input v-model="chatInput" type="textarea" :rows="2" maxlength="500"
                            placeholder="Ask a question, for example: What is the export price?"
                            @keydown.native="handleComposerKeydown"></el-input>
                        <el-button type="primary" :loading="sending" :disabled="!chatInput" @click="sendMessage">Send</el-button>
                    </div>
                    <p class="ai-chat-note">Final prices, specifications, lead times and certifications must be confirmed by our sales team.</p>
                </section>

                <aside v-if="leadMode" class="ai-lead-panel" aria-label="Share contact details">
                    <div class="ai-lead-heading">
                        <div><strong>Request Sales Follow-up</strong><span>Our team will contact you about products and cooperation.</span></div>
                        <button type="button" aria-label="Close contact form" @click="leadMode = false">×</button>
                    </div>
                    <el-form ref="leadForm" :model="form" :rules="rules" label-position="top">
                        <el-form-item label="Company Name" prop="company">
                            <el-input v-model="form.company" maxlength="35" placeholder="Enter your company name"></el-input>
                        </el-form-item>
                        <el-form-item label="Contact Name">
                            <el-input v-model="form.contact" maxlength="20" placeholder="Enter your name"></el-input>
                        </el-form-item>
                        <el-form-item label="Email" prop="email">
                            <el-input v-model="form.email" maxlength="45" placeholder="name@company.com"></el-input>
                        </el-form-item>
                        <el-form-item label="Phone">
                            <el-input v-model="form.phone" maxlength="22" placeholder="Phone number"></el-input>
                        </el-form-item>
                        <el-form-item label="WhatsApp">
                            <el-input v-model="form.whatsapp" maxlength="22" placeholder="WhatsApp number"></el-input>
                        </el-form-item>
                        <el-form-item label="Inquiry">
                            <el-input v-model="form.message" type="textarea" :rows="2" maxlength="100"
                                placeholder="Model, quantity, market or cooperation"></el-input>
                        </el-form-item>
                        <el-button class="ai-lead-submit" type="primary" :loading="submitting"
                            @click="submitLead">Submit Contact</el-button>
                        <p class="ai-privacy">Your details are used only for FGQ business follow-up.</p>
                    </el-form>
                </aside>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import { getTrackingContext } from '@/utils/customerTracking'

export default {
    name: 'AiChatService',
    props: { visible: { type: Boolean, default: false } },
    data() {
        return {
            proactiveVisible: false,
            proactiveTimer: null,
            productContext: '',
            conversationRoute: '',
            conversationId: '',
            chatInput: '',
            sending: false,
            messages: [],
            messageId: 0,
            leadMode: false,
            submitting: false,
            form: { company: '', contact: '', email: '', phone: '', whatsapp: '', message: '' },
            rules: {
                company: [{ required: true, message: 'Please enter your company name', trigger: 'blur' }],
                email: [{ type: 'email', message: 'Please enter a valid email address', trigger: 'blur' }]
            }
        }
    },
    computed: {
        dialogVisible: {
            get() { return this.visible },
            set(value) { this.$emit('update:visible', value) }
        },
        dialogWidth() {
            if (this.$store.state.winWidth <= 768) return '94%'
            return this.leadMode ? '940px' : '740px'
        },
        quickPrompts() {
            return this.productContext
                ? ['Get an export quote', 'Ask specifications', 'Shipping and lead time', 'Distributor cooperation']
                : ['Recommend a model', 'Get export prices', 'OEM / ODM cooperation', 'After-sales and parts']
        }
    },
    watch: {
        '$route.fullPath'() {
            this.proactiveVisible = false
            this.refreshProductContext()
            this.scheduleProactive()
        }
    },
    mounted() {
        this.refreshProductContext()
        this.scheduleProactive()
    },
    beforeDestroy() { window.clearTimeout(this.proactiveTimer) },
    methods: {
        refreshProductContext() {
            this.$nextTick(() => {
                window.setTimeout(() => {
                    const candidates = Array.from(document.querySelectorAll('h1, [data-product-name]'))
                    const heading = candidates.find(node => node && node.textContent && node.textContent.trim())
                    const title = (heading && heading.textContent.trim()) || document.title.split('|')[0].trim()
                    this.productContext = this.$route.name === 'show'
                        ? (title || `Product ${this.$route.query.id || ''}`.trim())
                        : ''
                }, 400)
            })
        },
        scheduleProactive() {
            window.clearTimeout(this.proactiveTimer)
            if (this.$route.name !== 'show') return
            const key = `fgq-ai-teaser:${this.$route.fullPath}`
            if (window.sessionStorage.getItem(key)) return
            this.proactiveTimer = window.setTimeout(() => {
                this.refreshProductContext()
                this.proactiveVisible = true
            }, 1800)
        },
        dismissProactive() {
            this.proactiveVisible = false
            window.sessionStorage.setItem(`fgq-ai-teaser:${this.$route.fullPath}`, '1')
        },
        openChat() {
            this.dismissProactive()
            this.dialogVisible = true
        },
        handleOpen() {
            this.refreshProductContext()
            if (this.conversationRoute !== this.$route.fullPath || !this.messages.length) {
                this.conversationRoute = this.$route.fullPath
                this.conversationId = ''
                this.messages = []
                const greeting = this.$route.name === 'show'
                    ? 'Hello, I am the FGQ AI product assistant. Tell me how you plan to use this vehicle, your destination market and estimated quantity. I can help check the suitable configuration first, then arrange sales follow-up for a confirmed quote.'
                    : 'Hello, I am the FGQ AI assistant. Are you looking for a cargo, passenger or sanitation electric tricycle? Tell me your use case, destination market and estimated quantity, and I will help narrow down suitable models.'
                this.addMessage('assistant', greeting)
            }
            this.scrollToBottom()
        },
        addMessage(role, text, suggestContact = false) {
            this.messages.push({ id: ++this.messageId, role, text, suggestContact })
            this.scrollToBottom()
        },
        sendPrompt(prompt) {
            this.chatInput = prompt
            this.sendMessage()
        },
        handleComposerKeydown(event) {
            if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault()
                this.sendMessage()
            }
        },
        sendMessage() {
            const text = this.chatInput.trim()
            if (!text || this.sending) return
            this.chatInput = ''
            this.addMessage('user', text)
            this.sending = true
            const history = this.messages
                .filter(item => item.role === 'user' || item.role === 'assistant')
                .slice(-8)
                .map(item => ({ role: item.role, content: item.text }))

            this.$api.aiChat({
                ...getTrackingContext('en'),
                language: 'en',
                message: text,
                product: this.productContext,
                page_context: this.collectPageContext(),
                conversation_id: this.conversationId,
                messages: history
            }).then(res => {
                const reply = res && res.data && res.data.reply
                if (!reply) throw new Error('empty reply')
                if (res.data.conversation_id) this.conversationId = res.data.conversation_id
                this.addMessage('assistant', reply, Boolean(res.data.suggest_contact))
            }).catch(() => {
                const fallback = this.localReply(text)
                this.addMessage('assistant', fallback.text, fallback.suggestContact)
            }).finally(() => {
                this.sending = false
                this.scrollToBottom()
            })
        },
        collectPageContext() {
            const main = document.querySelector('main') || document.querySelector('#app')
            if (!main) return ''
            return main.innerText.replace(/\s+/g, ' ').trim().slice(0, 1600)
        },
        localReply(text) {
            const product = this.productContext || 'this electric tricycle'
            const conversation = this.messages.map(item => item.text).join(' ')
            const userTurns = this.messages.filter(item => item.role === 'user').length
            const buyingIntent = /price|quote|cost|how much|quotation|buy|order|units?|pieces?|dealer|distributor|agent|oem|odm|ckd|skd|cooperat|ship|delivery|freight/i.test(conversation)
            const result = (reply, suggestContact = false) => ({ text: reply, suggestContact })
            if (/^(hi|hello|hey|good\s+(morning|afternoon|evening)|how are you)[\s!.?]*$/i.test(text)) {
                return result('Hello! I am ready to help. Are you looking for a cargo, passenger or sanitation electric tricycle?')
            }
            if (/recommend|which model|suitable model|choose|selection|use case/i.test(text)) {
                return result(`I can help recommend a suitable model. Will it mainly carry goods, passengers or sanitation equipment, and what load or road conditions do you expect for ${product}?`)
            }
            if (/price|quote|cost|how much|quotation/i.test(text)) {
                return result(`I can help request an export quotation for ${product}. Pricing depends on configuration and order quantity. What quantity and destination country or region should the quotation cover?`, true)
            }
            if (/spec|range|battery|motor|speed|load|size|dimension/i.test(text)) {
                return result(`Specifications vary by model and configuration. Which detail matters most for ${product} - battery, motor, range, speed, loading capacity or dimensions? I will not guess any unpublished value.`, userTurns >= 2)
            }
            if (/dealer|distributor|agent|oem|odm|ckd|skd|cooperat/i.test(text)) {
                return result('FGQ welcomes distributor, importer, OEM/ODM and CKD/SKD inquiries. Which market will you serve, and what is your estimated first-order quantity? Our sales team can then review the cooperation plan with you.', true)
            }
            if (/ship|delivery|freight|warranty|after.?sale|spare|parts/i.test(text)) {
                return result('Shipping, delivery time, warranty and spare-parts plans depend on the model, quantity and destination. Which country or port should we plan for? Sales can confirm the final arrangement after receiving your contact details.', true)
            }
            if (buyingIntent && /\b\d+\b|country|market|port|destination/i.test(text)) {
                return result('Thank you—that helps qualify the request. Please use the contact form to share an email or WhatsApp number, and our sales team can follow up with the confirmed model, quotation and delivery plan.', true)
            }
            return result(`I can help with model selection, specifications, quotations, cooperation, shipping and after-sales support for ${product}. What is your intended use and destination market?`, buyingIntent && userTurns >= 2)
        },
        scrollToBottom() {
            this.$nextTick(() => {
                const list = this.$refs.messageList
                if (list) list.scrollTop = list.scrollHeight
            })
        },
        resetLeadForm() {
            this.form = { company: '', contact: '', email: '', phone: '', whatsapp: '', message: '' }
            this.$nextTick(() => this.$refs.leadForm && this.$refs.leadForm.clearValidate())
        },
        openLeadForm() {
            if (!this.form.message) {
                this.form.message = this.messages
                    .filter(item => item.role === 'user')
                    .slice(-3)
                    .map(item => item.text.trim())
                    .filter(Boolean)
                    .join(' / ')
                    .slice(0, 100)
            }
            this.leadMode = true
        },
        normalizeLeadForm() {
            Object.keys(this.form).forEach(key => {
                this.form[key] = String(this.form[key] || '').replace(/\s+/g, ' ').trim()
            })
        },
        submitLead() {
            this.normalizeLeadForm()
            this.$refs.leadForm.validate(valid => {
                if (!valid) return
                if (!this.form.email && !this.form.phone && !this.form.whatsapp) {
                    this.$message.warning('Please provide an email, phone number or WhatsApp number')
                    return
                }
                this.submitting = true
                this.$api.addCustomerLead({
                    ...getTrackingContext('en'),
                    conversation_id: this.conversationId,
                    source: 'ai_chat',
                    company: this.form.company,
                    contact_name: this.form.contact,
                    email: this.form.email,
                    phone: this.form.phone,
                    im_type: this.form.whatsapp ? 'whatsapp' : '',
                    im_account: this.form.whatsapp,
                    product: this.productContext,
                    inquiry: this.form.message
                }).then(res => {
                    if (!res || res.code !== 1) throw new Error('submit failed')
                    this.$message.success('Thank you. Our team will contact you shortly.')
                    this.addMessage('assistant', 'Your contact details have been received. Our sales team will follow up, and you can continue asking questions here.')
                    this.leadMode = false
                    this.resetLeadForm()
                }).catch(() => {
                    this.$message.error('Unable to submit right now. Please try again or contact us by phone.')
                }).finally(() => { this.submitting = false })
            })
        }
    }
}
</script>

<style lang="scss">
.ai-chat-dialog {
    border-radius: 16px; overflow: hidden; background: #fff; box-shadow: 0 22px 70px rgba(0,0,0,.28);
    .el-dialog__header { padding: 20px 24px 14px; border-bottom: 1px solid #eceff1; }
    .el-dialog__body { padding: 0; }
    .el-dialog__headerbtn { top: 18px; right: 18px; }
    .el-form-item { margin-bottom: 12px; }
    .el-form-item__label { padding-bottom: 2px; font-weight: 600; line-height: 20px; }
    .el-button--primary { background: #b00d16; border-color: #b00d16; }
}
.ai-chat-title { display:flex; align-items:center; gap:12px; padding-right:30px; }
.ai-chat-title > img { width:42px; height:42px; object-fit:contain; }
.ai-chat-title > div { min-width:0; display:flex; flex:1; flex-direction:column; gap:3px; }
.ai-chat-title strong { color:#171717; font-size:20px; }
.ai-chat-title span { overflow:hidden; color:#6c747a; font-size:13px; text-overflow:ellipsis; white-space:nowrap; }
.ai-contact-button { flex-shrink:0; padding:8px 12px; border:1px solid #b00d16; border-radius:999px; background:#fff; color:#b00d16; font-size:13px; cursor:pointer; }
.ai-chat-layout { position:relative; display:flex; height:min(620px,76vh); background:#f7f8f9; }
.ai-chat-panel { min-width:0; display:flex; flex:1; flex-direction:column; }
.ai-message-list { flex:1; padding:22px 24px 12px; overflow-y:auto; scroll-behavior:smooth; }
.ai-message { display:flex; align-items:flex-start; gap:9px; margin-bottom:14px; }
.ai-message > img { width:30px; height:30px; object-fit:contain; }
.ai-message-bubble { max-width:76%; display:flex; flex-direction:column; align-items:flex-start; padding:11px 14px; border-radius:4px 14px 14px; background:#fff; color:#25282b; font-size:14px; line-height:1.65; box-shadow:0 2px 10px rgba(0,0,0,.06); }
.ai-message-bubble > span { white-space:pre-wrap; }
.ai-message-contact { align-self:flex-start; margin-top:9px; padding:6px 10px; border:1px solid #b00d16; border-radius:999px; background:#fff; color:#b00d16; font:inherit; font-size:12px; line-height:1.2; cursor:pointer; }
.ai-message.is-user { justify-content:flex-end; }
.ai-message.is-user .ai-message-bubble { border-radius:14px 4px 14px 14px; background:#b00d16; color:#fff; box-shadow:none; }
.ai-typing { display:flex; gap:4px; align-items:center; min-height:38px; }
.ai-typing i { width:6px; height:6px; border-radius:50%; background:#a3a7aa; animation:ai-dot 1.1s infinite ease-in-out; }
.ai-typing i:nth-child(2){animation-delay:.15s}.ai-typing i:nth-child(3){animation-delay:.3s}
@keyframes ai-dot{0%,60%,100%{transform:translateY(0)}30%{transform:translateY(-5px)}}
.ai-quick-prompts { display:flex; gap:8px; padding:8px 20px; overflow-x:auto; }
.ai-quick-prompts button { flex-shrink:0; padding:7px 11px; border:1px solid #d9dde1; border-radius:999px; background:#fff; color:#4f575e; font-size:12px; cursor:pointer; }
.ai-quick-prompts button:hover { border-color:#b00d16; color:#b00d16; }
.ai-composer { display:flex; align-items:flex-end; gap:10px; padding:10px 20px 8px; border-top:1px solid #e1e4e7; background:#fff; }
.ai-composer .el-textarea { flex:1; }.ai-composer .el-textarea__inner{resize:none}.ai-composer .el-button{height:42px}
.ai-chat-note { margin:0; padding:0 20px 12px; background:#fff; color:#92989d; font-size:11px; }
.ai-lead-panel { width:320px; flex-shrink:0; padding:20px; overflow-y:auto; border-left:1px solid #e1e4e7; background:#fff; }
.ai-lead-heading { display:flex; justify-content:space-between; gap:10px; margin-bottom:14px; }
.ai-lead-heading > div { display:flex; flex-direction:column; gap:4px; }
.ai-lead-heading strong { color:#202326; font-size:17px; }.ai-lead-heading span{color:#737b81;font-size:12px;line-height:1.5}
.ai-lead-heading button { align-self:flex-start; border:0; background:transparent; color:#8b9297; font-size:22px; cursor:pointer; }
.ai-lead-submit { width:100%; }.ai-privacy{margin:10px 0 0;color:#8b9297;font-size:11px;line-height:1.5}
.ai-product-teaser { position:fixed; right:90px; top:calc(50% - 25px); z-index:97; width:350px; overflow:hidden; border:1px solid rgba(176,13,22,.16); border-radius:14px; background:#fff; box-shadow:0 14px 38px rgba(0,0,0,.2); }
.ai-teaser-main { width:100%; display:flex; align-items:center; gap:11px; padding:14px 34px 14px 14px; border:0; background:transparent; text-align:left; cursor:pointer; }
.ai-teaser-main img{width:36px;height:36px;object-fit:contain}.ai-teaser-main span{min-width:0;display:flex;flex-direction:column;gap:3px}
.ai-teaser-main strong{color:#202326;font-size:14px}.ai-teaser-main small{color:#687078;font-size:12px;line-height:1.45}
.ai-teaser-close{position:absolute;top:6px;right:8px;border:0;background:transparent;color:#9aa0a5;font-size:18px;cursor:pointer}
.ai-teaser-fade-enter-active,.ai-teaser-fade-leave-active{transition:opacity .25s ease,transform .25s ease}.ai-teaser-fade-enter,.ai-teaser-fade-leave-to{opacity:0;transform:translateX(18px)}
@media(max-width:768px){
    .ai-chat-dialog{max-height:88vh;margin-top:5vh!important;display:flex;flex-direction:column;
        .el-dialog__header{flex:0 0 auto;padding:14px 14px 10px}.el-dialog__body{min-height:0;flex:1 1 auto}.el-dialog__headerbtn{top:12px;right:10px;font-size:18px}
        .el-form-item{margin-bottom:10px}.el-form-item__label{padding-bottom:2px;font-size:13px;line-height:18px}.el-input__inner{height:38px;padding:0 10px;font-size:14px;line-height:38px}.el-textarea__inner{padding:9px 10px;font-size:14px;line-height:1.5}.el-button{padding:9px 12px;font-size:14px}}
    .ai-chat-title{gap:8px;padding-right:22px}.ai-chat-title>img{width:34px;height:34px}.ai-chat-title strong{font-size:16px}.ai-chat-title span{max-width:145px;font-size:10px}.ai-contact-button{padding:6px 7px;font-size:10px}
    .ai-chat-layout{height:76vh}.ai-message-list{padding:14px 12px 8px}.ai-message{gap:6px;margin-bottom:10px}.ai-message>img{width:27px;height:27px}.ai-message-bubble{max-width:83%;padding:9px 11px;font-size:14px;line-height:1.55}
    .ai-quick-prompts{gap:6px;padding:6px 10px}.ai-quick-prompts button{padding:6px 9px;font-size:12px}.ai-composer{gap:7px;padding:8px 10px 6px}.ai-composer .el-button{height:40px}.ai-chat-note{padding:0 10px 8px;font-size:10px;line-height:1.4}
    .ai-lead-panel{position:absolute;inset:8px;z-index:3;width:auto;padding:16px;border:1px solid #e1e4e7;border-radius:12px;box-shadow:0 12px 36px rgba(0,0,0,.2)}.ai-lead-heading strong{font-size:16px}.ai-lead-heading span{font-size:11px}.ai-privacy{font-size:10px}
    .ai-product-teaser{right:12px;top:auto;bottom:18px;width:calc(100vw - 24px);border-radius:12px}.ai-teaser-main{gap:9px;padding:11px 32px 11px 12px}.ai-teaser-main img{width:32px;height:32px}.ai-teaser-main strong{font-size:14px}.ai-teaser-main small{font-size:11px}
}
</style>
